import React, { useEffect, useRef, useState } from 'react';
import Lightbox from 'yet-another-react-lightbox';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';

import {
  decodeHtmlEntities,
  cleanListingTitle,
  formatHeightString,
  formatSize,
} from '../../../../util/string';
import { richTextParagraphs } from '../../../../util/richText';
import useHasVerticalOverflow from '../../../../hooks/useHasVerticalOverflow';

import css from './ReviewContent.module.css';
import neutralPlaceholder from './images/neutral-placeholder.png';

const hasReviewMetaValue = value => {
  if (!value) return false;

  const normalizedValue = String(value)
    .trim()
    .toLowerCase();
  return normalizedValue && normalizedValue !== 'n/a';
};

const ReviewContent = ({ reviewData, onReady }) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const detailsColumnRef = useRef(null);
  const productDetailsRef = useRef(null);

  const hasReviewData = Boolean(reviewData?.attributes);
  const attributes = reviewData?.attributes || {};
  const { description, publicData = {} } = attributes;
  // Images are at the top level of the denormalized entity, not in attributes
  const images = reviewData?.images || [];

  // Get the first image or use a neutral placeholder fallback
  const getListingImages = () => {
    if (images && images.length > 0) {
      return images.map(image => {
        const variants = image.attributes?.variants;
        if (variants) {
          // Try common variant names in order of preference
          const variantNames = [
            'scaled-large',
            'listing-card',
            'default',
            'listing-image',
            'scaled-small',
            'scaled-medium',
          ];
          for (const variantName of variantNames) {
            if (variants[variantName]?.url) {
              return variants[variantName].url;
            }
          }
          // If no known variant found, try to get first available variant
          const firstVariant = Object.values(variants)[0];
          if (firstVariant?.url) {
            return firstVariant.url;
          }
        }
        // Only use placeholder if truly no image variants exist
        return neutralPlaceholder;
      });
    }
    // Use a clearly neutral placeholder when no review image is provided.
    return [neutralPlaceholder];
  };

  const listingImages = getListingImages();
  const selectedImage = listingImages[selectedImageIndex];
  const hasReviewImages = images.length > 0;
  const lightboxSlides = hasReviewImages ? listingImages.map(src => ({ src })) : [];
  const reviewId = reviewData?.id?.uuid || null;
  const brandName = decodeHtmlEntities(publicData.BrandName || 'Unknown Brand');
  const title = cleanListingTitle(attributes.title || 'Review', brandName);

  // Clean up the description for display
  const getCleanDescription = description => {
    if (!description) return 'No review content available.';

    // Remove HTML tags and clean up
    const cleanDescription = description
      .replace(/<[^>]*>/g, '') // Remove HTML tags
      .replace(/&nbsp;/g, ' ') // Replace &nbsp; with spaces
      .replace(/&amp;/g, '&') // Replace &amp; with &
      .replace(/&lt;/g, '<') // Replace &lt; with <
      .replace(/&gt;/g, '>') // Replace &gt; with >
      .replace(/＜abbr[^＞]*＞.*?＜\/abbr＞/gi, '') // Full-width brackets
      .trim();

    return cleanDescription || 'No review content available.';
  };

  const cleanDescription = getCleanDescription(description);
  const descriptionParagraphs = richTextParagraphs(
    cleanDescription,
    { linkify: true },
    css.reviewTextParagraph
  );
  const uppercaseBrandName = String(brandName || '').toUpperCase();
  const reviewSize = formatSize(publicData);
  const reviewHeight = hasReviewMetaValue(publicData.height)
    ? formatHeightString(publicData.height)
    : null;
  const reviewMetaPills = [
    hasReviewMetaValue(reviewSize) ? { label: 'Size', value: reviewSize } : null,
    hasReviewMetaValue(reviewHeight) ? { label: 'Height', value: reviewHeight } : null,
  ].filter(Boolean);
  const hasDetailOverflow = useHasVerticalOverflow(detailsColumnRef, productDetailsRef, [
    cleanDescription,
    reviewId,
    reviewMetaPills.length,
    selectedImage,
    title,
  ]);

  useEffect(() => {
    setSelectedImageIndex(0);
    setIsLightboxOpen(false);
  }, [reviewId]);

  useEffect(() => {
    if (!hasReviewData || typeof onReady !== 'function') {
      return undefined;
    }

    let isCancelled = false;

    const preloadMedia = async () => {
      const preloadTasks = listingImages.map(image => {
        if (!image) {
          return Promise.resolve();
        }

        return new Promise(resolve => {
          const preloadImage = new window.Image();
          preloadImage.onload = () => resolve();
          preloadImage.onerror = () => resolve();
          preloadImage.src = image;
        });
      });

      await Promise.all(preloadTasks);

      if (!isCancelled) {
        onReady();
      }
    };

    preloadMedia();

    return () => {
      isCancelled = true;
    };
  }, [hasReviewData, listingImages, onReady, reviewId]);

  if (!hasReviewData) {
    return (
      <div className={css.twoColumnGrid}>
        <div className={css.loading}>Loading review content...</div>
      </div>
    );
  }

  return (
    <>
      <p className={css.reviewEyebrow} data-type-preview-role="review-eyebrow">
        Community review
      </p>
      <div className={`${css.reviewHeaderBlock} ${css.mobileReviewHeader}`}>
        <div className={css.brandTag}>
          <span className={css.brandName}>{uppercaseBrandName}</span>
        </div>
        <h1 className={css.productTitle}>{title}</h1>
        {reviewMetaPills.length > 0 ? (
          <div className={css.reviewMetaPills} aria-label="Review fit details">
            {reviewMetaPills.map(({ label, value }) => (
              <span className={css.reviewMetaPill} key={label}>
                <span className={css.reviewMetaPillLabel}>{label}</span>
                <span className={css.reviewMetaPillValue}>{value}</span>
              </span>
            ))}
          </div>
        ) : null}
      </div>
      <div className={css.twoColumnGrid}>
        <div
          className={`${css.mediaColumn} ${
            listingImages.length > 1 ? css.mediaColumnWithThumbnails : ''
          }`}
        >
          <div className={css.mainImageContainer}>
            {hasReviewImages ? (
              <button
                type="button"
                className={css.mainImageButton}
                onClick={() => setIsLightboxOpen(true)}
                aria-label={`Open review image ${selectedImageIndex + 1} of ${
                  listingImages.length
                }`}
              >
                <img
                  className={css.backgroundImage}
                  src={selectedImage}
                  alt={publicData.slug || attributes.title}
                />
              </button>
            ) : (
              <img
                className={css.backgroundImage}
                src={selectedImage}
                alt={'No review image available'}
              />
            )}
            <span className={css.visuallyHidden} aria-hidden={false}>
              {images && images.length > 0
                ? publicData.slug || attributes.title
                : 'No review image available'}
            </span>
          </div>
          {listingImages.length > 1 && (
            <div className={css.thumbnailsContainer}>
              {listingImages.slice(0, 5).map((image, index) => (
                <div
                  key={index}
                  className={`${css.thumbnail} ${
                    selectedImageIndex === index ? css.activeThumbnail : ''
                  }`}
                  onClick={() => setSelectedImageIndex(index)}
                >
                  <img src={image} className={css.thumbnailBg} />
                </div>
              ))}
            </div>
          )}
        </div>

        <div
          ref={detailsColumnRef}
          className={`${css.detailsColumn} ${hasDetailOverflow ? css.detailsColumnScrollable : ''}`}
        >
          <div ref={productDetailsRef} className={css.productDetails}>
            <div className={`${css.reviewHeaderBlock} ${css.desktopReviewHeader}`}>
              <div className={css.brandTag}>
                <span className={css.brandName}>{uppercaseBrandName}</span>
              </div>
              <h1 className={css.productTitle}>{title}</h1>
              {reviewMetaPills.length > 0 ? (
                <div className={css.reviewMetaPills} aria-label="Review fit details">
                  {reviewMetaPills.map(({ label, value }) => (
                    <span className={css.reviewMetaPill} key={label}>
                      <span className={css.reviewMetaPillLabel}>{label}</span>
                      <span className={css.reviewMetaPillValue}>{value}</span>
                    </span>
                  ))}
                </div>
              ) : null}
            </div>
            <div className={css.reviewText}>
              {descriptionParagraphs.length > 0 ? (
                descriptionParagraphs
              ) : (
                <p className={css.reviewTextParagraph}>No review content available.</p>
              )}
            </div>
          </div>
        </div>
      </div>
      {lightboxSlides.length > 0 ? (
        <Lightbox
          open={isLightboxOpen}
          close={() => setIsLightboxOpen(false)}
          index={selectedImageIndex}
          slides={lightboxSlides}
          plugins={[Zoom]}
          zoom={{ maxZoomPixelRatio: 3, scrollToZoom: true }}
          controller={{ closeOnBackdropClick: true }}
          carousel={{ finite: lightboxSlides.length <= 1 }}
          on={{
            view: ({ index }) => setSelectedImageIndex(index),
          }}
        />
      ) : null}
    </>
  );
};

export default ReviewContent;
